/**
 * Give a recorded WebM the duration its writer left out.
 *
 * Chrome's MediaRecorder streams a WebM as it goes, so it cannot know the
 * length in advance and writes none: the Segment has an "unknown" size and the
 * Info block has no Duration. A media element opening such a file reports
 * `duration` as Infinity, which probeFile treats as unreadable, and seeking in
 * it is slower because the browser has to scan for the end. The recorder knows
 * how long it ran, so the number is written in here before the file is saved.
 *
 * Only the Info block is rewritten. Everything after it is the same bytes at a
 * slightly later offset, and the Segment's own size is left alone when it is
 * unknown, which is the case this exists for. The clusters are never read, so
 * a long recording costs a few kilobytes of work, not a copy of the file.
 */

const ID_EBML = 0x1a45dfa3;
const ID_SEGMENT = 0x18538067;
const ID_INFO = 0x1549a966;
const ID_CLUSTER = 0x1f43b675;
const ID_TIMECODE_SCALE = 0x2ad7b1;
const ID_DURATION = 0x4489;

/** Nanoseconds per timecode tick when Info does not say. */
const DEFAULT_TIMECODE_SCALE = 1_000_000;

/** Info sits within the first few kilobytes; this is generous. */
const HEAD_BYTES = 256 * 1024;

interface Element {
  id: number;
  /** Where the element begins: the first byte of its id. */
  at: number;
  /** Where its payload begins. */
  dataAt: number;
  /** Payload length, or -1 when the file says "unknown". */
  size: number;
  /** How many bytes the size took, so a rewrite can keep the same width. */
  sizeLength: number;
}

/** True when this looks like a WebM the patch applies to. */
export const isWebm = (type: string): boolean => /^(video|audio)\/webm\b/.test(type);

/**
 * The file with a Duration in its Info block. Unchanged when it already has
 * one, when it is not WebM, or when its layout is not what a recorder writes.
 */
export async function withDuration(blob: Blob, seconds: number): Promise<Blob> {
  if (!isWebm(blob.type) || !Number.isFinite(seconds) || seconds <= 0) return blob;
  const head = new Uint8Array(await blob.slice(0, Math.min(blob.size, HEAD_BYTES)).arrayBuffer());
  const patched = patchHead(head, seconds, blob.size);
  if (!patched) return blob;
  return new Blob([patched.bytes, blob.slice(patched.consumed)], { type: blob.type });
}

/**
 * The rewritten bytes up to the end of Info, and how much of the original
 * they replace. Exported for the tests, which build small files by hand.
 */
export function patchHead(
  head: Uint8Array,
  seconds: number,
  total: number,
): { bytes: Uint8Array<ArrayBuffer>; consumed: number } | null {
  const ebml = readElement(head, 0);
  if (!ebml || ebml.id !== ID_EBML || ebml.size < 0) return null;
  const segment = readElement(head, ebml.dataAt + ebml.size);
  if (!segment || segment.id !== ID_SEGMENT) return null;

  let at = segment.dataAt;
  let info: Element | null = null;
  while (at < head.length) {
    const child = readElement(head, at);
    if (!child || child.size < 0) return null;
    if (child.id === ID_INFO) {
      info = child;
      break;
    }
    if (child.id === ID_CLUSTER) return null;
    at = child.dataAt + child.size;
  }
  if (!info || info.dataAt + info.size > head.length) return null;

  let scale = DEFAULT_TIMECODE_SCALE;
  at = info.dataAt;
  const infoEnd = info.dataAt + info.size;
  while (at < infoEnd) {
    const child = readElement(head, at);
    if (!child || child.size < 0 || child.dataAt + child.size > infoEnd) return null;
    if (child.id === ID_TIMECODE_SCALE) scale = readUint(head, child.dataAt, child.size);
    // A duration the writer put there is better information than ours.
    if (child.id === ID_DURATION) return null;
    at = child.dataAt + child.size;
  }

  const ticks = (seconds * 1e9) / scale;
  const duration = new Uint8Array(2 + 1 + 8);
  duration.set([0x44, 0x89, 0x88]);
  new DataView(duration.buffer).setFloat64(3, ticks);

  const body = new Uint8Array(info.size + duration.length);
  body.set(head.subarray(info.dataAt, infoEnd));
  body.set(duration, info.size);
  const sizeBytes = encodeVint(body.length, info.sizeLength);
  const grownBy = sizeBytes.length - info.sizeLength + duration.length;

  // The Segment's size, when it is known, has to grow with its contents. It is
  // rewritten in place at the same width; a recorder that wrote a known size
  // wrote eight bytes, so there is always room.
  const before = head.slice(0, info.at);
  if (segment.size >= 0) {
    const resized = encodeVint(segment.size + grownBy, segment.sizeLength);
    if (resized.length !== segment.sizeLength) return null;
    before.set(resized, segment.at + idLength(head[segment.at] ?? 0));
  }
  if (total > 0 && info.dataAt + info.size > total) return null;

  const idBytes = head.subarray(info.at, info.at + idLength(head[info.at] ?? 0));
  const bytes = new Uint8Array(before.length + idBytes.length + sizeBytes.length + body.length);
  bytes.set(before, 0);
  bytes.set(idBytes, before.length);
  bytes.set(sizeBytes, before.length + idBytes.length);
  bytes.set(body, before.length + idBytes.length + sizeBytes.length);
  return { bytes, consumed: infoEnd };
}

/** How many bytes an element id occupies, from its first byte's leading bit. */
function idLength(first: number): number {
  if (first & 0x80) return 1;
  if (first & 0x40) return 2;
  if (first & 0x20) return 3;
  if (first & 0x10) return 4;
  return 0;
}

function readElement(bytes: Uint8Array, at: number): Element | null {
  const first = bytes[at];
  if (first === undefined) return null;
  const idLen = idLength(first);
  if (idLen === 0 || at + idLen > bytes.length) return null;
  const id = readUint(bytes, at, idLen);
  const size = readVint(bytes, at + idLen);
  if (!size) return null;
  return { id, at, dataAt: at + idLen + size.length, size: size.unknown ? -1 : size.value, sizeLength: size.length };
}

/** A size: the leading 1 marks the width and is not part of the value. */
function readVint(bytes: Uint8Array, at: number): { value: number; length: number; unknown: boolean } | null {
  const first = bytes[at];
  if (first === undefined || first === 0) return null;
  let length = 1;
  while (((first << (length - 1)) & 0x80) === 0) length += 1;
  if (at + length > bytes.length) return null;
  const mask = 0xff >> length;
  let value = first & mask;
  let allOnes = (first & mask) === mask;
  for (let i = 1; i < length; i += 1) {
    const byte = bytes[at + i] ?? 0;
    value = value * 256 + byte;
    allOnes = allOnes && byte === 0xff;
  }
  return { value, length, unknown: allOnes };
}

/** A size vint no narrower than `minLength`, and wider only when it must be. */
function encodeVint(value: number, minLength: number): Uint8Array {
  let length = Math.max(1, minLength);
  while (length < 8 && value >= 2 ** (7 * length) - 1) length += 1;
  const out = new Uint8Array(length);
  let rest = value;
  for (let i = length - 1; i >= 0; i -= 1) {
    out[i] = rest % 256;
    rest = Math.floor(rest / 256);
  }
  out[0] = (out[0] ?? 0) | (0x80 >> (length - 1));
  return out;
}

function readUint(bytes: Uint8Array, at: number, length: number): number {
  let value = 0;
  for (let i = 0; i < length; i += 1) value = value * 256 + (bytes[at + i] ?? 0);
  return value;
}
